package br.com.htms.inspeccaocampo.auth

import androidx.compose.runtime.NoLiveLiterals
import br.com.htms.inspeccaocampo.platform.R2Photos
import kotlin.coroutines.resume
import kotlinx.browser.localStorage
import kotlinx.coroutines.suspendCancellableCoroutine
import org.khronos.webgl.ArrayBuffer
import org.khronos.webgl.Uint8Array
import org.khronos.webgl.get
import org.w3c.files.Blob
import org.w3c.files.BlobPropertyBag
import org.w3c.xhr.FormData

@NoLiveLiterals
actual object AuthApi {
    private const val TOKEN_KEY = "icd_auth_token"
    private const val EXP_KEY = "icd_auth_exp"
    private const val USER_KEY = "icd_auth_user"

    private var session: AuthSession? = null

    actual fun isLoginRequired(): Boolean = R2Photos.configOrNull() != null

    actual fun currentSession(): AuthSession? = session

    actual suspend fun restoreSession(): AuthSession? {
        val token = localStorage.getItem(TOKEN_KEY) ?: return null
        val exp = localStorage.getItem(EXP_KEY)?.toDoubleOrNull()?.toLong() ?: 0L
        val now = (js("Date.now()") as Number).toLong()
        if (exp > 0 && now > exp) {
            logout()
            return null
        }
        val cachedUser = localStorage.getItem(USER_KEY)
        if (!cachedUser.isNullOrBlank()) {
            parseUserJson(cachedUser)?.let { u ->
                session = AuthSession(token = token, expiresAt = exp, user = u)
            }
        }
        val me = fetchMe(token) ?: run {
            logout()
            return null
        }
        session = AuthSession(token = token, expiresAt = exp, user = me)
        persist(session!!)
        return session
    }

    actual suspend fun login(username: String, password: String): Result<AuthSession> {
        val base = apiBase() ?: return Result.failure(IllegalStateException("API não configurada"))
        return try {
            val payload =
                """{"username":${jsonStr(username)},"password":${jsonStr(password)}}"""
            val text = httpJson("POST", "$base/auth/login", payload, null)
            val parsed = JSON.parse<dynamic>(text)
            if (parsed.token == null) {
                val err = (parsed.error as? String) ?: "Falha no login"
                return Result.failure(IllegalStateException(err))
            }
            val userDyn = parsed.user
            val user = AuthUser(
                id = userDyn.id as String,
                username = userDyn.username as String,
                role = userDyn.role as String,
                profileTypes = parseProfileTypesDyn(userDyn),
                createdAt = (userDyn.createdAt as? Number)?.toLong()
            )
            val s = AuthSession(
                token = parsed.token as String,
                expiresAt = (parsed.expiresAt as Number).toLong(),
                user = user
            )
            session = s
            persist(s)
            Result.success(s)
        } catch (t: Throwable) {
            Result.failure(t)
        }
    }

    actual fun logout() {
        session = null
        runCatching {
            localStorage.removeItem(TOKEN_KEY)
            localStorage.removeItem(EXP_KEY)
            localStorage.removeItem(USER_KEY)
        }
    }

    actual suspend fun listInspections(): Result<List<RemoteInspection>> {
        val s = session ?: return Result.failure(IllegalStateException("Não autenticado"))
        val base = apiBase() ?: return Result.failure(IllegalStateException("API não configurada"))
        return try {
            val text = httpJson("GET", "$base/inspections", null, s.token)
            val parsed = JSON.parse<dynamic>(text)
            val arr = parsed.inspections ?: return Result.success(emptyList())
            val out = mutableListOf<RemoteInspection>()
            val len = arr.length as Int
            for (i in 0 until len) {
                val it = arr[i]
                val summary = it.summary ?: js("{}")
                out.add(
                    RemoteInspection(
                        id = it.id as String,
                        localId = (it.localId as? Number)?.toLong(),
                        createdAt = (it.createdAt as Number).toLong(),
                        username = it.username as? String,
                        summaryTst = (summary.tstResponsavel as? String) ?: "",
                        summaryLocal = (summary.localTexto as? String) ?: "",
                        summaryEfetivo = when (val e = summary.efetivo) {
                            is Number -> e.toString()
                            is String -> e
                            else -> null
                        },
                        summaryObra = summary.obra as? String,
                        dataHorarioMs = (summary.dataHorarioMs as? Number)?.toLong(),
                        pdfFileName = summary.pdfFileName as? String,
                        excelFileName = summary.excelFileName as? String,
                        formType = summary.formType as? String,
                        hasPdf = it.hasPdf == true,
                        hasExcel = it.hasExcel == true,
                        hasJson = it.hasJson == true
                    )
                )
            }
            Result.success(out)
        } catch (t: Throwable) {
            Result.failure(t)
        }
    }

    actual suspend fun uploadInspection(
        localId: Long,
        createdAt: Long,
        summaryTst: String,
        summaryLocal: String,
        summaryEfetivo: String?,
        summaryObra: String?,
        dataHorarioMs: Long?,
        formType: String?,
        pdfFileName: String?,
        excelFileName: String?,
        pdfBytes: ByteArray?,
        excelBytes: ByteArray?,
        jsonText: String?
    ): Result<String> {
        val s = session ?: return Result.failure(IllegalStateException("Não autenticado"))
        val base = apiBase() ?: return Result.failure(IllegalStateException("API não configurada"))
        return try {
            val fd = FormData()
            val pdfName = pdfFileName?.takeIf { it.isNotBlank() } ?: "inspecao.pdf"
            val excelName = excelFileName?.takeIf { it.isNotBlank() } ?: "inspecao.xlsx"
            val metaStr = buildString {
                append("{\"localId\":$localId,\"createdAt\":$createdAt,\"summary\":{")
                append("\"tstResponsavel\":${jsonStr(summaryTst)},")
                append("\"localTexto\":${jsonStr(summaryLocal)},")
                append("\"efetivo\":")
                if (summaryEfetivo == null) append("null") else append(jsonStr(summaryEfetivo))
                append(",\"obra\":")
                if (summaryObra.isNullOrBlank()) append("null") else append(jsonStr(summaryObra))
                append(",\"dataHorarioMs\":")
                if (dataHorarioMs == null) append("null") else append(dataHorarioMs)
                append(",\"formType\":")
                if (formType.isNullOrBlank()) append("null") else append(jsonStr(formType))
                append(",\"pdfFileName\":")
                if (pdfFileName.isNullOrBlank()) append("null") else append(jsonStr(pdfName))
                append(",\"excelFileName\":")
                if (excelFileName.isNullOrBlank()) append("null") else append(jsonStr(excelName))
                append("}}")
            }
            fd.append("meta", metaStr)
            if (pdfBytes != null && pdfBytes.isNotEmpty()) {
                formAppendBlob(fd, "pdf", bytesToBlob(pdfBytes, "application/pdf"), pdfName)
            }
            if (excelBytes != null && excelBytes.isNotEmpty()) {
                formAppendBlob(
                    fd,
                    "excel",
                    bytesToBlob(
                        excelBytes,
                        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                    ),
                    excelName
                )
            }
            if (!jsonText.isNullOrBlank()) {
                formAppendBlob(
                    fd,
                    "json",
                    bytesToBlob(jsonText.encodeToByteArray(), "application/json"),
                    "inspecao.json"
                )
            }
            val text = httpForm("POST", "$base/inspections", fd, s.token)
            val parsed = JSON.parse<dynamic>(text)
            val id = parsed.id as? String
            if (id.isNullOrBlank()) {
                val err = (parsed.error as? String) ?: "Falha no upload"
                Result.failure(IllegalStateException(err))
            } else {
                Result.success(id)
            }
        } catch (t: Throwable) {
            Result.failure(t)
        }
    }

    actual suspend fun downloadInspectionBytes(remoteId: String, kind: String): Result<ByteArray> {
        val s = session ?: return Result.failure(IllegalStateException("Não autenticado"))
        val base = apiBase() ?: return Result.failure(IllegalStateException("API não configurada"))
        return try {
            val bytes = httpBytes("GET", "$base/inspections/$remoteId/$kind", s.token)
            if (bytes.isEmpty()) Result.failure(IllegalStateException("Arquivo vazio"))
            else Result.success(bytes)
        } catch (t: Throwable) {
            Result.failure(t)
        }
    }

    actual suspend fun deleteInspection(remoteId: String): Result<Unit> {
        val s = session ?: return Result.failure(IllegalStateException("Não autenticado"))
        val base = apiBase() ?: return Result.failure(IllegalStateException("API não configurada"))
        return try {
            val id = encodeUriComponent(remoteId)
            // POST evita bloqueio de DELETE em alguns proxies/browsers
            httpJson("POST", "$base/inspections/$id/delete", "{}", s.token)
            Result.success(Unit)
        } catch (t: Throwable) {
            Result.failure(friendlyNetworkError(t))
        }
    }

    actual suspend fun listUsers(): Result<List<AuthUserListItem>> {
        val s = session ?: return Result.failure(IllegalStateException("Não autenticado"))
        val base = apiBase() ?: return Result.failure(IllegalStateException("API não configurada"))
        return try {
            val text = httpJson("GET", "$base/users", null, s.token)
            val parsed = JSON.parse<dynamic>(text)
            val arr = parsed.users ?: return Result.success(emptyList())
            val out = mutableListOf<AuthUserListItem>()
            val len = arr.length as Int
            for (i in 0 until len) {
                val it = arr[i]
                out.add(
                    AuthUserListItem(
                        id = it.id as String,
                        username = it.username as String,
                        role = it.role as String,
                        profileTypes = parseProfileTypesDyn(it),
                        createdAt = (it.createdAt as? Number)?.toLong()
                    )
                )
            }
            Result.success(out)
        } catch (t: Throwable) {
            Result.failure(t)
        }
    }

    actual suspend fun createUser(
        username: String,
        password: String,
        role: String,
        profileTypes: List<String>
    ): Result<AuthUserListItem> {
        val s = session ?: return Result.failure(IllegalStateException("Não autenticado"))
        val base = apiBase() ?: return Result.failure(IllegalStateException("API não configurada"))
        return try {
            val typesJson = ProfileTypes.normalize(profileTypes).joinToString(",") { jsonStr(it) }
            val payload =
                """{"username":${jsonStr(username)},"password":${jsonStr(password)},"role":${jsonStr(role)},"profileTypes":[$typesJson]}"""
            val text = httpJson("POST", "$base/users", payload, s.token)
            val parsed = JSON.parse<dynamic>(text)
            val u = parsed.user
            if (u == null) {
                val err = (parsed.error as? String) ?: "Falha ao criar usuário"
                Result.failure(IllegalStateException(err))
            } else {
                Result.success(
                    AuthUserListItem(
                        id = u.id as String,
                        username = u.username as String,
                        role = u.role as String,
                        profileTypes = parseProfileTypesDyn(u),
                        createdAt = (u.createdAt as? Number)?.toLong()
                    )
                )
            }
        } catch (t: Throwable) {
            Result.failure(t)
        }
    }

    actual suspend fun updateUser(
        userId: String,
        role: String?,
        profileTypes: List<String>?,
        password: String?
    ): Result<AuthUserListItem> {
        val s = session ?: return Result.failure(IllegalStateException("Não autenticado"))
        val base = apiBase() ?: return Result.failure(IllegalStateException("API não configurada"))
        return try {
            val parts = mutableListOf<String>()
            if (role != null) parts.add("\"role\":${jsonStr(role)}")
            if (profileTypes != null) {
                val typesJson = ProfileTypes.normalize(profileTypes).joinToString(",") { jsonStr(it) }
                parts.add("\"profileTypes\":[$typesJson]")
            }
            if (!password.isNullOrBlank()) parts.add("\"password\":${jsonStr(password)}")
            val payload = "{${parts.joinToString(",")}}"
            val id = encodeUriComponent(userId)
            val text = httpJson("PATCH", "$base/users/$id", payload, s.token)
            val parsed = JSON.parse<dynamic>(text)
            val u = parsed.user
            if (u == null) {
                val err = (parsed.error as? String) ?: "Falha ao atualizar usuário"
                Result.failure(IllegalStateException(err))
            } else {
                Result.success(
                    AuthUserListItem(
                        id = u.id as String,
                        username = u.username as String,
                        role = u.role as String,
                        profileTypes = parseProfileTypesDyn(u),
                        createdAt = (u.createdAt as? Number)?.toLong()
                    )
                )
            }
        } catch (t: Throwable) {
            Result.failure(t)
        }
    }

    actual suspend fun deleteUser(userId: String): Result<Unit> {
        val s = session ?: return Result.failure(IllegalStateException("Não autenticado"))
        val base = apiBase() ?: return Result.failure(IllegalStateException("API não configurada"))
        return try {
            val id = encodeUriComponent(userId)
            // POST evita bloqueio de DELETE em alguns proxies/browsers (TypeError: Failed to fetch)
            httpJson("POST", "$base/users/$id/delete", "{}", s.token)
            Result.success(Unit)
        } catch (t: Throwable) {
            Result.failure(friendlyNetworkError(t))
        }
    }

    actual suspend fun listObrasAtivas(): Result<List<br.com.htms.inspeccaocampo.data.ObraItem>> {
        val base = apiBase() ?: return Result.failure(IllegalStateException("API não configurada"))
        val token = session?.token
            ?: return Result.failure(IllegalStateException("Não autenticado"))
        return try {
            val text = httpJson("GET", "$base/obras", null, token)
            Result.success(parseObras(text))
        } catch (t: Throwable) {
            Result.failure(t)
        }
    }

    actual suspend fun listObrasAdmin(): Result<List<br.com.htms.inspeccaocampo.data.ObraItem>> {
        val s = session ?: return Result.failure(IllegalStateException("Não autenticado"))
        val base = apiBase() ?: return Result.failure(IllegalStateException("API não configurada"))
        return try {
            val text = httpJson("GET", "$base/obras/manage", null, s.token)
            Result.success(parseObras(text))
        } catch (t: Throwable) {
            Result.failure(t)
        }
    }

    actual suspend fun createObra(nome: String): Result<br.com.htms.inspeccaocampo.data.ObraItem> {
        val s = session ?: return Result.failure(IllegalStateException("Não autenticado"))
        val base = apiBase() ?: return Result.failure(IllegalStateException("API não configurada"))
        return try {
            val text = httpJson(
                "POST",
                "$base/obras",
                """{"nome":${jsonStr(nome)}}""",
                s.token
            )
            val parsed = JSON.parse<dynamic>(text)
            val o = parsed.obra
            if (o == null) {
                val err = (parsed.error as? String) ?: "Falha ao criar obra"
                Result.failure(IllegalStateException(err))
            } else {
                Result.success(obraFromDyn(o))
            }
        } catch (t: Throwable) {
            Result.failure(t)
        }
    }

    actual suspend fun updateObra(
        id: String,
        nome: String?,
        ativo: Boolean?
    ): Result<br.com.htms.inspeccaocampo.data.ObraItem> {
        val s = session ?: return Result.failure(IllegalStateException("Não autenticado"))
        val base = apiBase() ?: return Result.failure(IllegalStateException("API não configurada"))
        return try {
            val payload = buildString {
                append('{')
                var first = true
                if (nome != null) {
                    append("\"nome\":${jsonStr(nome)}")
                    first = false
                }
                if (ativo != null) {
                    if (!first) append(',')
                    append("\"ativo\":$ativo")
                }
                append('}')
            }
            val text = httpJson("PATCH", "$base/obras/$id", payload, s.token)
            val parsed = JSON.parse<dynamic>(text)
            val o = parsed.obra
            if (o == null) {
                val err = (parsed.error as? String) ?: "Falha ao atualizar obra"
                Result.failure(IllegalStateException(err))
            } else {
                Result.success(obraFromDyn(o))
            }
        } catch (t: Throwable) {
            Result.failure(t)
        }
    }

    private fun parseObras(text: String): List<br.com.htms.inspeccaocampo.data.ObraItem> {
        val parsed = JSON.parse<dynamic>(text)
        val arr = parsed.obras ?: return emptyList()
        val out = mutableListOf<br.com.htms.inspeccaocampo.data.ObraItem>()
        val len = arr.length as Int
        for (i in 0 until len) {
            out.add(obraFromDyn(arr[i]))
        }
        return out
    }

    private fun obraFromDyn(o: dynamic): br.com.htms.inspeccaocampo.data.ObraItem =
        br.com.htms.inspeccaocampo.data.ObraItem(
            id = o.id as String,
            nome = o.nome as String,
            ativo = o.ativo != false,
            sortOrder = (o.sortOrder as? Number)?.toInt() ?: 0
        )

    private fun apiBase(): String? = R2Photos.configOrNull()?.baseUrl

    private fun encodeUriComponent(value: String): String =
        js("encodeURIComponent")(value) as String

    /** Mensagem legível quando o browser aborta por rede/CORS (não chega JSON do Worker). */
    private fun friendlyNetworkError(t: Throwable): Throwable {
        val raw = (t.message ?: t.toString()).trim()
        val lower = raw.lowercase()
        if (
            lower.contains("failed to fetch") ||
            lower.contains("networkerror") ||
            lower.contains("network request failed") ||
            lower.contains("load failed") ||
            lower == "rede" ||
            lower.contains("typeerror")
        ) {
            return IllegalStateException(
                "Falha de rede ou CORS ao contatar a API. Verifique a conexão e tente de novo."
            )
        }
        return t
    }

    private fun throwableFromJsErr(err: dynamic): Throwable {
        val raw = try {
            (err?.message as? String)?.takeIf { it.isNotBlank() }
                ?: err?.toString()?.takeIf { it.isNotBlank() && it != "[object Object]" }
                ?: "rede"
        } catch (_: Throwable) {
            "rede"
        }
        return friendlyNetworkError(Exception(raw))
    }

    private fun persist(s: AuthSession) {
        localStorage.setItem(TOKEN_KEY, s.token)
        localStorage.setItem(EXP_KEY, s.expiresAt.toString())
        val typesJson = ProfileTypes.normalize(s.user.profileTypes).joinToString(",") { jsonStr(it) }
        localStorage.setItem(
            USER_KEY,
            """{"id":${jsonStr(s.user.id)},"username":${jsonStr(s.user.username)},"role":${jsonStr(s.user.role)},"profileTypes":[$typesJson]}"""
        )
    }

    private fun parseProfileTypesDyn(u: dynamic): List<String> {
        val raw = u.profileTypes ?: return listOf(ProfileTypes.TST)
        val asString = raw as? String
        if (asString != null) {
            return ProfileTypes.fromEncoded(asString).ifEmpty { listOf(ProfileTypes.TST) }
        }
        val collected = mutableListOf<String>()
        val len = (raw.length as? Number)?.toInt() ?: 0
        for (i in 0 until len) {
            val v = raw[i] as? String
            if (v != null) collected.add(v)
        }
        return ProfileTypes.normalize(collected).ifEmpty { listOf(ProfileTypes.TST) }
    }

    private fun parseUserJson(raw: String): AuthUser? = try {
        val u = JSON.parse<dynamic>(raw)
        AuthUser(
            id = u.id as String,
            username = u.username as String,
            role = u.role as String,
            profileTypes = parseProfileTypesDyn(u)
        )
    } catch (_: Throwable) {
        null
    }

    private suspend fun fetchMe(token: String): AuthUser? {
        val base = apiBase() ?: return null
        return try {
            val text = httpJson("GET", "$base/auth/me", null, token)
            val parsed = JSON.parse<dynamic>(text)
            val u = parsed.user ?: return null
            AuthUser(
                id = u.id as String,
                username = u.username as String,
                role = u.role as String,
                profileTypes = parseProfileTypesDyn(u),
                createdAt = (u.createdAt as? Number)?.toLong()
            )
        } catch (_: Throwable) {
            null
        }
    }

    private fun jsonStr(s: String): String =
        "\"" + s.replace("\\", "\\\\").replace("\"", "\\\"")
            .replace("\n", "\\n").replace("\r", "\\r") + "\""

    private fun bytesToBlob(bytes: ByteArray, mime: String): Blob {
        val u8 = Uint8Array(bytes.size)
        u8.set(Array(bytes.size) { bytes[it] })
        return Blob(arrayOf(u8), BlobPropertyBag(type = mime))
    }

    private fun formAppendBlob(fd: FormData, name: String, blob: Blob, filename: String) {
        // Kotlin/JS FormData tipagem nem sempre expõe filename
        fd.asDynamic().append(name, blob, filename)
    }

    private suspend fun httpJson(
        method: String,
        url: String,
        body: String?,
        token: String?
    ): String = suspendCancellableCoroutine { cont ->
        try {
            val opts = js("{}")
            opts.method = method
            opts.mode = "cors"
            opts.headers = js("{}")
            opts.headers["Accept"] = "application/json"
            if (token != null) opts.headers["Authorization"] = "Bearer $token"
            if (body != null) {
                opts.headers["Content-Type"] = "application/json"
                opts.body = body
            }
            val promise: dynamic = js("fetch")(url, opts)
            promise.then({ response: dynamic ->
                response.text().then({ text: dynamic ->
                    val status = response.status as Int
                    if (status >= 400) {
                        val errMsg = try {
                            val p = JSON.parse<dynamic>(text as String)
                            (p.error as? String) ?: (text as String)
                        } catch (_: Throwable) {
                            text as String
                        }
                        cont.resumeWith(Result.failure(IllegalStateException(errMsg)))
                    } else {
                        cont.resume(text as String)
                    }
                }, { err: dynamic ->
                    cont.resumeWith(Result.failure(throwableFromJsErr(err)))
                })
            }, { err: dynamic ->
                cont.resumeWith(Result.failure(throwableFromJsErr(err)))
            })
        } catch (t: Throwable) {
            cont.resumeWith(Result.failure(friendlyNetworkError(t)))
        }
    }

    private suspend fun httpForm(
        method: String,
        url: String,
        form: FormData,
        token: String
    ): String = suspendCancellableCoroutine { cont ->
        try {
            val opts = js("{}")
            opts.method = method
            opts.mode = "cors"
            opts.headers = js("{}")
            opts.headers["Accept"] = "application/json"
            opts.headers["Authorization"] = "Bearer $token"
            opts.body = form
            val promise: dynamic = js("fetch")(url, opts)
            promise.then({ response: dynamic ->
                response.text().then({ text: dynamic ->
                    val status = response.status as Int
                    if (status >= 400) {
                        val errMsg = try {
                            val p = JSON.parse<dynamic>(text as String)
                            (p.error as? String) ?: (text as String)
                        } catch (_: Throwable) {
                            text as String
                        }
                        cont.resumeWith(Result.failure(IllegalStateException(errMsg)))
                    } else {
                        cont.resume(text as String)
                    }
                }, { err: dynamic ->
                    cont.resumeWith(Result.failure(throwableFromJsErr(err)))
                })
            }, { err: dynamic ->
                cont.resumeWith(Result.failure(throwableFromJsErr(err)))
            })
        } catch (t: Throwable) {
            cont.resumeWith(Result.failure(friendlyNetworkError(t)))
        }
    }

    private suspend fun httpBytes(method: String, url: String, token: String): ByteArray =
        suspendCancellableCoroutine { cont ->
            try {
                val opts = js("{}")
                opts.method = method
                opts.headers = js("{}")
                opts.headers["Authorization"] = "Bearer $token"
                val promise: dynamic = js("fetch")(url, opts)
                promise.then({ response: dynamic ->
                    if ((response.status as Int) >= 400) {
                        response.text().then({ text: dynamic ->
                            cont.resumeWith(
                                Result.failure(IllegalStateException(text as? String ?: "erro"))
                            )
                        }, { err: dynamic ->
                            cont.resumeWith(Result.failure(Exception("$err")))
                        })
                    } else {
                        response.arrayBuffer().then({ buf: ArrayBuffer ->
                            val u8 = Uint8Array(buf)
                            val out = ByteArray(u8.length)
                            for (i in 0 until u8.length) out[i] = u8[i]
                            cont.resume(out)
                        }, { err: dynamic ->
                            cont.resumeWith(Result.failure(Exception("$err")))
                        })
                    }
                }, { err: dynamic ->
                    cont.resumeWith(Result.failure(Exception("$err")))
                })
            } catch (t: Throwable) {
                cont.resumeWith(Result.failure(t))
            }
        }
}
